import { useCallback } from 'react';
import { useSignal } from '@funtech-int/rival-react';

import {
    ContentMarkType,
    PlayerImpressionEntity,
} from '@kinopoisk-int/evgen-web-events/web/named_enums';
import { playerMovieImpressionMarked } from '@kinopoisk-int/evgen-web-events/web/player';

import { usePlayerInstance } from '@/scenarios/legacy-player/lib/contexts/instance';
import { useLogger } from '@/shared/hooks/useLogger';
import { Dependencies } from '@/scenarios/legacy-player/hooks/analytics/use-dependencies';
import { useEventCallback } from '@/scenarios/legacy-player/hooks/analytics/use-event-callback';
import { useMovieUGC } from '@/shared/hooks/useMovieUGC';
import { useMoviePlannedToWatch } from '@/shared/hooks/useMoviePlannedToWatch';

interface UiParams {
    mainUuid: string | undefined | null;
    promotedMovieTitle?: string | null;
}

interface Params {
    uiParams: UiParams;
    dependencies: Dependencies;
    callbackParams: [{ vsid: string; markType: ContentMarkType; uuid: string }];
}

interface EpisodeFavoriteToggleParams {
    seriesId?: string | null;
    promotedMovieId: number;
}

const useLogToggleEpisodeFavoriteButtonActivation = (uiParams: UiParams) =>
    useEventCallback({
        event: playerMovieImpressionMarked,
        uiParams,
        eventParamsBuilder: prerollContent,
    });

const prerollContent: (params: Params) => Parameters<typeof playerMovieImpressionMarked>[1] = ({
    uiParams: { mainUuid, promotedMovieTitle },
    callbackParams: [{ vsid, markType, uuid }],
}) => ({
    markType,
    entityType: PlayerImpressionEntity.Preroll,
    uuid,
    title: promotedMovieTitle ?? '-',
    vsid,
    mainUuid: mainUuid ?? '-',
});

export const useEpisodeFavoriteToggleWithAnalytics = (params: EpisodeFavoriteToggleParams) => {
    const { seriesId, promotedMovieId } = params;

    const { title: promotedMovieTitle, contentId: promotedContentId } = useMoviePlannedToWatch({
        movieId: promotedMovieId,
    });

    const movieUGC = useMovieUGC(promotedMovieId);

    const isPlannedToWatch = useSignal(movieUGC.plannedToWatch);

    const logToggleFilmFavorite = useLogToggleEpisodeFavoriteButtonActivation({
        mainUuid: seriesId,
        promotedMovieTitle,
    });

    const logger = useLogger();

    const { vsid } = usePlayerInstance() || {};

    const onEpisodeFavoriteToggleWithAnalylics = useCallback(() => {
        if (!promotedContentId || typeof promotedContentId !== 'string') {
            logger.error(
                {
                    promotedContentId,
                    isPlannedToWatch,
                    vsid,
                    promotedMovieId,
                    type: 'episode',
                },
                'Tried to add title to watch later with invalid promotedMovieid'
            );
            return;
        }

        logToggleFilmFavorite({
            vsid: vsid || '',
            markType: isPlannedToWatch ? ContentMarkType.Unbookmarked : ContentMarkType.Bookmarked,
            uuid: promotedMovieId.toString(),
        });

        movieUGC.togglePlannedToWatch({ toastViewVariant: 'TOP' });
    }, [
        promotedContentId,
        logToggleFilmFavorite,
        vsid,
        isPlannedToWatch,
        promotedMovieId,
        movieUGC,
        logger,
    ]);

    return { onEpisodeFavoriteToggleWithAnalylics, isPlannedToWatch, promotedContentId };
};
